import { FormGroup, Validators } from "@angular/forms";
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs';
import { format } from "date-fns";
import { CofaceClaimFormControls, CofaceControls, CofaceForm } from "../form.controls";
import { FormFactory } from "../form.factory";
import { Summary } from "../form.resources";
import { UpdateCofaceParams } from "../form.service";

export function getCofaceForm(this: FormFactory): CofaceForm {
  const data = this.data as Summary;

  const form = this.fb.group<CofaceControls>({
    debtor_party_type_id: this.fb.control(data.debtor.party_type_id ?? null, { validators: Validators.required }),
    claims: this.fb.array<FormGroup<CofaceClaimFormControls>>([]),
    terms_accepted: this.fb.control(!!data.client.terms_accepted_at, { validators: Validators.requiredTrue, nonNullable: true }),
  });

  for (const claim of data.claims) {
    form.controls.claims.push(this.fb.group<CofaceClaimFormControls>({
      id: this.fb.control(claim.id, { nonNullable: true }),
      due_date_at: this.fb.control(claim.due_date_at, { nonNullable: true }),
      original_amount: this.fb.control(claim.original_amount, { nonNullable: true }),
      currency_iso: this.fb.control(claim.currency_iso, { nonNullable: true }),
      file: this.fb.control(claim.file ?? null, { validators: Validators.required }),
      coface_invoice_type_code: this.fb.control(claim.coface_invoice_type_code ?? null, { validators: Validators.required }),
      issued_at: this.fb.control(claim.issued_at ?? null, { validators: Validators.required }),
    }));
  }

  form.valueChanges
    .pipe(
      debounceTime(1000),
      filter(() => form.valid),
      map(v => JSON.stringify(v)),
      distinctUntilChanged(),
    )
    .subscribe({
      next: async () => {
        try {
          const formValue = form.getRawValue();
          const params: UpdateCofaceParams = {
            claims: form.controls.claims.getRawValue().map(c => ({
              id: c.id,
              coface_invoice_type_code: c.coface_invoice_type_code!,
              invoice_date: c.issued_at instanceof Date
                ? format(c.issued_at, 'yyyy-MM-dd')
                : c.issued_at!,
              file: c.file instanceof File ? c.file : undefined,
            })),
            debtor_party_type_id: formValue.debtor_party_type_id!,
            terms_accepted: formValue.terms_accepted,
          };
          await this.formService.updateCofaceForm(params);
        } catch (error) {
          console.error('Error saving coface form data', error);
        }
      },
    });

  return form;
}
